<template>
  <v-main>
    <v-container fluid class="rag-container">
      <div class="rag-breadcrumbs">
        <Breadcrumbs />
      </div>

      <v-card class="rag-hero-card" elevation="4">
        <!-- Header -->
        <div class="rag-hero-header">
          <div class="d-flex align-center">
            <v-avatar size="52" class="mr-4 rag-logo">
              <v-icon size="30" color="white">mdi-robot-outline</v-icon>
            </v-avatar>
            <div>
              <h1 class="rag-hero-title">AI Standards Assistant</h1>
              <p class="rag-hero-subtitle">
                Powered by CertifAI · Ask anything about cybersecurity standards
              </p>
            </div>
          </div>
        </div>

        <v-card-text class="pa-8">
          <v-row>
            <v-col cols="12" md="7">
              <h2 class="rag-section-title mb-3">What is the AI Standards Assistant?</h2>
              <p class="rag-body-text mb-4">
                The <strong>AI Standards Assistant</strong> is an intelligent chatbot trained on
                cybersecurity standards such as <strong>IEC 62443</strong> and the
                <strong>Cyber Resilience Act (CRA)</strong>. It helps you:
              </p>
              <ul class="rag-feature-list mb-5">
                <li>
                  <v-icon small color="#0097a7" class="mr-2">mdi-check-circle</v-icon>
                  Understand complex standard requirements in plain language
                </li>
                <li>
                  <v-icon small color="#0097a7" class="mr-2">mdi-check-circle</v-icon>
                  Get guidance on how to meet specific compliance clauses
                </li>
                <li>
                  <v-icon small color="#0097a7" class="mr-2">mdi-check-circle</v-icon>
                  Find relevant articles and sections across standards
                </li>
                <li>
                  <v-icon small color="#0097a7" class="mr-2">mdi-check-circle</v-icon>
                  Ask follow-up questions and explore concepts interactively
                </li>
              </ul>

              <!-- Credentials hint -->
              <v-alert type="info" outlined dense class="rag-credentials-hint mb-5" border="left">
                <div class="d-flex align-start">
                  <v-icon color="info" class="mr-2 mt-1" small>mdi-key-outline</v-icon>
                  <div>
                    <strong>Login hint:</strong> Use the same username and password as this platform
                    (CertifAI credentials). The AI Standards Assistant is part of the CertifAI
                    ecosystem and shares the same account system.
                  </div>
                </div>
              </v-alert>

              <v-btn color="#0097a7" dark large class="rag-launch-btn" @click="launchAssistant">
                <v-icon left>mdi-open-in-new</v-icon>
                Launch AI Standards Assistant
              </v-btn>
              <div class="rag-hint mt-2">Opens in a new tab</div>
            </v-col>

            <v-col cols="12" md="5" class="d-flex align-center justify-center">
              <div class="rag-visual-box">
                <div class="rag-orb-ring"></div>
                <v-icon size="80" color="#0097a7" class="rag-orb-icon">mdi-robot-outline</v-icon>
                <div class="rag-sample-messages">
                  <div class="rag-bubble">
                    <v-icon x-small color="#0097a7" class="mr-1">mdi-help-circle-outline</v-icon>
                    What does IEC 62443-4-2 require for patch management?
                  </div>
                  <div class="rag-bubble rag-bubble--reply">
                    <v-icon x-small color="white" class="mr-1">mdi-robot-outline</v-icon>
                    IEC 62443-4-2 CR 3.2 requires that the component supports...
                  </div>
                  <div class="rag-bubble">
                    <v-icon x-small color="#0097a7" class="mr-1">mdi-help-circle-outline</v-icon>
                    How does the CRA address vulnerability disclosure?
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>

<script>
import Breadcrumbs from '@/components/charts/Breadcrumbs.vue';

export default {
  name: 'RagAnalyzer',
  components: { Breadcrumbs },
  methods: {
    launchAssistant() {
      const url =
        process.env.VUE_APP_RAG_ANALYZER_URL || 'https://chatbot.certifai.digital.tecnalia.dev';
      window.open(url, '_blank', 'noopener,noreferrer');
    },
  },
};
</script>

<style scoped>
.rag-container {
  max-width: 1100px;
  margin: 0 auto;
}

.rag-breadcrumbs {
  display: flex;
  justify-content: center;
  padding: 16px 0 24px;
}

.rag-hero-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.rag-hero-header {
  background: linear-gradient(135deg, #00405e 0%, #006494 50%, #0097a7 100%);
  padding: 24px 32px;
}

.rag-hero-title {
  color: white;
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
}

.rag-hero-subtitle {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.95rem;
  margin: 4px 0 0;
}

.rag-logo {
  background: rgba(255, 255, 255, 0.2) !important;
}

.rag-section-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a2332;
}

.rag-body-text {
  color: #455a64;
  font-size: 0.97rem;
  line-height: 1.7;
}

.rag-feature-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.rag-feature-list li {
  display: flex;
  align-items: flex-start;
  padding: 5px 0;
  font-size: 0.93rem;
  color: #455a64;
  line-height: 1.5;
}

.rag-credentials-hint {
  border-radius: 10px !important;
  font-size: 0.9rem;
}

.rag-launch-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 1rem !important;
  letter-spacing: 0 !important;
  box-shadow: 0 4px 16px rgba(0, 151, 167, 0.35) !important;
  transition: all 0.3s ease !important;
}

.rag-launch-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(0, 151, 167, 0.5) !important;
}

.rag-hint {
  font-size: 0.75rem;
  color: #90a4ae;
}

/* Visual box */
.rag-visual-box {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  width: 100%;
}

.rag-orb-ring {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: conic-gradient(from 0deg, #0097a7, #6d28d9, #0097a7);
  opacity: 0.1;
  animation: rag-spin 8s linear infinite;
}

@keyframes rag-spin {
  to {
    transform: translateX(-50%) rotate(360deg);
  }
}

.rag-orb-icon {
  position: relative;
  z-index: 1;
  margin-bottom: 20px;
  opacity: 0.18;
}

.rag-sample-messages {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.rag-bubble {
  background: #f0fdfe;
  border: 1.5px solid #b2ebf2;
  border-radius: 12px;
  padding: 10px 14px;
  font-size: 0.8rem;
  color: #37474f;
  display: flex;
  align-items: flex-start;
}

.rag-bubble--reply {
  background: linear-gradient(135deg, #00405e, #0097a7);
  border: none;
  color: white;
  margin-left: 16px;
}
</style>
